import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftRight, Search, RefreshCw } from "lucide-react";
import { fetchLocations, getCachedLocations } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { LocationPicker } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const today = () => new Date().toISOString().slice(0, 10);
const AIRPORT_IDS = ["ayt", "gzp"];

export const BookingSearch = ({ initial = {} }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [locations, setLocations] = useState(() => getCachedLocations());
  const [locError, setLocError] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [from, setFrom] = useState(initial.from || "ayt");
  const [to, setTo] = useState(initial.to || "");
  const [date, setDate] = useState(initial.date || today());
  const [time, setTime] = useState(initial.time || "10:00");
  const [pax, setPax] = useState(Number(initial.pax) || 2);
  const [round, setRound] = useState(initial.rt === "1");
  const parseRet = (v) => (v && v.includes("T") ? [v.split("T")[0], (v.split("T")[1] || "").slice(0, 5)] : ["", ""]);
  const [retDate, setRetDate] = useState(parseRet(initial.ret || "")[0]);
  const [retTime, setRetTime] = useState(parseRet(initial.ret || "")[1] || "18:00");
  // Which side is the airport picker. The other side lists hotels/places.
  const [airportSide, setAirportSide] = useState(
    AIRPORT_IDS.includes(initial.to) && !AIRPORT_IDS.includes(initial.from) ? "to" : "from"
  );

  const loadLocations = useCallback(() => {
    setLocError(false);
    setLocLoading(true);
    fetchLocations()
      .then((locs) => {
        if (locs.length) setLocations(locs);
      })
      .catch(() => {
        // Keep any cached list; only surface an error if we have nothing to show.
        if (getCachedLocations().length === 0) setLocError(true);
      })
      .finally(() => setLocLoading(false));
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  // One side lists only airports (AYT/GZP); the other lists only hotels/places.
  const airportOptions = useMemo(() => locations.filter((l) => l.type === "airport"), [locations]);
  const placeOptions = useMemo(() => locations.filter((l) => l.type !== "airport"), [locations]);
  const fromOptions = airportSide === "from" ? airportOptions : placeOptions;
  const toOptions = airportSide === "to" ? airportOptions : placeOptions;

  const swap = () => {
    setFrom(to);
    setTo(from);
    setAirportSide((s) => (s === "from" ? "to" : "from"));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!from || !to || !date) return toast.error(t("search.errFill"));
    if (from === to) return toast.error(t("search.errSame"));
    if (!AIRPORT_IDS.includes(from) && !AIRPORT_IDS.includes(to)) return toast.error(t("search.errAirport"));
    if (round && (!retDate || !retTime)) return toast.error(t("search.errRet"));
    const q = new URLSearchParams({
      from,
      to,
      date,
      time,
      pax: String(pax),
      rt: round ? "1" : "0",
    });
    if (round) q.set("ret", `${retDate}T${retTime}`);
    navigate(`/results?${q.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="booking-search-form"
      className="rounded-xl border border-slate-200 bg-white p-5 ambient sm:p-7"
    >
      <p className="label-eyebrow">{t("search.title")}</p>

      {locError && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="location-load-error">
          <p className="text-sm text-amber-800">{t("search.locError")}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="location-retry-btn"
            disabled={locLoading}
            onClick={loadLocations}
            className="border-amber-300 text-amber-800 hover:bg-amber-100"
          >
            <RefreshCw size={14} className={`mr-1.5 ${locLoading ? "animate-spin" : ""}`} /> {t("search.retry")}
          </Button>
        </div>
      )}

      <div className="mt-5 flex gap-2 rounded-md bg-slate-100 p-1 text-sm w-full sm:w-auto sm:inline-flex">
        <button
          type="button"
          data-testid="trip-oneway-btn"
          onClick={() => setRound(false)}
          className={`flex-1 rounded px-4 py-1.5 font-medium transition-colors duration-200 sm:flex-none ${
            !round ? "bg-white text-sea shadow-sm" : "text-slate-500"
          }`}
        >
          {t("search.oneway")}
        </button>
        <button
          type="button"
          data-testid="trip-round-btn"
          onClick={() => setRound(true)}
          className={`flex-1 rounded px-4 py-1.5 font-medium transition-colors duration-200 sm:flex-none ${
            round ? "bg-white text-sea shadow-sm" : "text-slate-500"
          }`}
        >
          {t("search.round")}
        </button>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-12">
        <div className="md:col-span-5">
          <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.from")}</Label>
          <LocationPicker
            locations={fromOptions}
            value={from}
            onChange={setFrom}
            placeholder={t("search.pick")}
            searchPlaceholder={airportSide === "from" ? t("search.pick") : t("search.searchHotel")}
            remote={airportSide !== "from"}
            testId="location-from"
          />
        </div>

        <div className="flex items-center justify-center md:col-span-1 md:items-end md:pb-1">
          <button
            type="button"
            data-testid="swap-locations-btn"
            aria-label={t("search.swap")}
            onClick={swap}
            className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors duration-200 hover:border-sea hover:text-sea"
          >
            <ArrowLeftRight size={15} className="rotate-90 md:rotate-0" />
          </button>
        </div>

        <div className="md:col-span-6">
          <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.to")}</Label>
          <LocationPicker
            locations={toOptions}
            value={to}
            onChange={setTo}
            placeholder={t("search.pick")}
            searchPlaceholder={airportSide === "to" ? t("search.pick") : t("search.searchHotel")}
            remote={airportSide !== "to"}
            testId="location-to"
          />
        </div>

        <div className="md:col-span-3">
          <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.date")}</Label>
          <input
            data-testid="pickup-date-input"
            type="date"
            value={date}
            min={today()}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm outline-none transition-colors duration-200 focus:border-sea"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.time")}</Label>
          <input
            data-testid="pickup-time-input"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm outline-none transition-colors duration-200 focus:border-sea"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.pax")}</Label>
          <input
            data-testid="pax-input"
            type="number"
            min={1}
            max={50}
            value={pax}
            onChange={(e) => setPax(Number(e.target.value))}
            className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm outline-none transition-colors duration-200 focus:border-sea"
          />
        </div>
        <div className="md:col-span-3">
          {round ? (
            <>
              <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.retDate")}</Label>
              <input
                data-testid="return-date-input"
                type="date"
                value={retDate}
                min={date || today()}
                onChange={(e) => setRetDate(e.target.value)}
                className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm outline-none transition-colors duration-200 focus:border-sea"
              />
            </>
          ) : (
            <div className="hidden md:block" />
          )}
        </div>
        {round && (
          <div className="md:col-span-3">
            <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{t("search.retTime")}</Label>
            <input
              data-testid="return-time-input"
              type="time"
              value={retTime}
              onChange={(e) => setRetTime(e.target.value)}
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm outline-none transition-colors duration-200 focus:border-sea"
            />
          </div>
        )}

        <div className="md:col-span-12">
          <Button
            data-testid="search-submit-btn"
            type="submit"
            className="h-12 w-full rounded-md bg-gold text-base font-semibold text-white transition-colors duration-200 hover:bg-gold-dark md:w-auto md:px-10"
          >
            <Search size={17} className="mr-2" />
            {t("search.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
};
